/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';

import {
    DEFAULT_LOCALE,
    KAVERI_GLOBAL_CATCH_NAME,
    KAVERI_GLOBAL_CATCH_RETRY_LIMIT,
} from '../../config';
import { DeviceModel } from '../../core/device';
import { DimensionScaler } from '../../core/device/DimensionScaler';
import { ErrorTypes } from '../../core/error';
import { Locale } from '../../core/locale/Locale';
import { RestartReasonsOperationsLogger } from '../../core/logging';
import { Log } from '../../core/logging/Log';
import { LocationUrls } from '../../core/navigation/LocationUrls';
import { RootState, useTivoDispatch, useTivoSelector } from '../../core/tivo/tivo-redux-wrapper';
import { CcbSwitch, CcbSwitchImpl } from '../../core/utils/ccb';
import { CcbSwitchName } from '../../core/utils/ccb/enums';
import { appStart } from '../../store/appl/appl.reducers';
import { sos } from '../../store/error/erroroverlays.reducers';
import {
    clearSystemHealthStatsInterval,
    logSystemHealthStats,
} from '../../store/healthstats/systemhealthstats.reducers';
import { setLocationUrl } from '../../store/urlchangeevents/urlchangeevents.reducers';
import { VisibilityState } from '../../store/visibilityevents/visibilitystate';
import { InfoCard } from '../common';
import { DebugScreen } from '../DebugOverlay';
import {
    CursorEvents,
    KeyboardEvents,
    MouseEvents,
    NetworkChangeEvents,
    SystemInfoEvent,
    URLChangeEvents,
    VisibilityEvents,
} from '../Events';
import { useGlobalCatch } from '../hooks';
import { OverlayDialog } from '../OverlayDialog';
import { VideoPlayer } from '../VideoPlayer';
import AppRoutes from './AppRoutes';

import './App.scss';
import { isFreshInstallLaunch } from '../../core/storage/FreshInstall';
import { clearAppStorage } from '../../core/storage/ClearAppStorage';
import { removeFreshInstallFromUrl } from '../../core/storage/RemoveFreshInstall';

const App: FunctionComponent = (): JSX.Element => {
    const TAG = 'App';

    const dispatch = useTivoDispatch();

    // TODO: Need to remove from redux store.
    // const { isApplicationReady } = useSelector((state: RootState) => state.appl);

    const [isApplicationReady, setIsApplicationReady] = useState(false);
    const { visibilityState } = useTivoSelector((state: RootState) => state.visibilityEvents);

    const globalCatches = useGlobalCatch({
        name: KAVERI_GLOBAL_CATCH_NAME,
        onCatch: DeviceModel.restartApplication,
        retryLimit: KAVERI_GLOBAL_CATCH_RETRY_LIMIT,
    });

    const initCcbSwitch = () => {
        const oCcbSwitchImpl = new CcbSwitchImpl();
        oCcbSwitchImpl
            .initialize()
            .then(() => {
                CcbSwitch.setCcbSwitchImpl(oCcbSwitchImpl);
                if (CcbSwitch.getInstance().isEnabled(CcbSwitchName.ENABLE_DEVELOPER_SANDBOX)) {
                    Log.setLogLevel(Log.LogLevel.INFO);
                }
            })
            .catch((error) => {
                Log.e(TAG, 'Failed to initalize CcbSwitch', error);
            });
    };

    useEffect(() => {
        if (visibilityState === VisibilityState.ON_PAUSE) {
            RestartReasonsOperationsLogger.getInstance().updateAppState(
                RestartReasonsOperationsLogger.AppState.BACKGROUND,
            );
        } else if (visibilityState === VisibilityState.ON_RESUME) {
            RestartReasonsOperationsLogger.getInstance().updateAppState(
                RestartReasonsOperationsLogger.AppState.RUNNING,
            );
        }
    }, [visibilityState]);

    useEffect(() => {
        if (isFreshInstallLaunch()) {
            Log.i(TAG, 'freshInstall=true — clearing all app storage');
            clearAppStorage();
            removeFreshInstallFromUrl();
        }
        initCcbSwitch();
        try {
            DimensionScaler.initialize(window.innerWidth, window.innerHeight);
        } catch (error) {
            Log.e(TAG, 'Failed to initialize DimensionScaler', error);
        }
        Locale.setLocale(DEFAULT_LOCALE);
        DeviceModel.initialize()
            .then(() => {
                setIsApplicationReady(true);
                Log.i(TAG, 'DeviceModel initialized successfully');
            })
            .catch(() => {
                Log.w(
                    TAG,
                    'Failed to initialize DeviceModel. Starting application with default properties.',
                );
            })
            .finally(() => {
                const isValidConfig = DeviceModel.getTiVoSettings().isValidMsoConfig();
                if (!isValidConfig) {
                    dispatch(sos(ErrorTypes.SOS_MESSAGE_CODE_H113));
                } else if (DeviceModel.isValidDevice()) {
                    if (globalCatches.isRetriesDone()) {
                        dispatch(sos(ErrorTypes.SOS_MESSAGE_CODE_H111));
                    } else {
                        dispatch(appStart());
                    }
                } else {
                    window.open('./404.html', '_self');
                }
            });
        return () => {
            dispatch(logSystemHealthStats());
            dispatch(clearSystemHealthStatsInterval());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isApplicationReady) dispatch(setLocationUrl(LocationUrls.SPLASH_SCREEN));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isApplicationReady]);

    return (
        <React.Fragment>
            <NetworkChangeEvents />
            <MouseEvents />
            <CursorEvents />
            <SystemInfoEvent />
            <KeyboardEvents>
                {/* TODO: need to remove the isApplicationReady check from here */}
                {isApplicationReady ? (
                    <React.Fragment>
                        <VideoPlayer />
                        <DebugScreen />
                        <BrowserRouter basename={DeviceModel.getAppHomePage()}>
                            <URLChangeEvents />
                            <VisibilityEvents />
                            {/* TODO: replace browserRoutes with createBrowserRouter */}
                            <AppRoutes />
                        </BrowserRouter>
                    </React.Fragment>
                ) : null}
            </KeyboardEvents>
            <OverlayDialog />
            {CcbSwitch.getInstance().isEnabledAndOverriden(
                CcbSwitchName.ENABLE_INFO_CARD_REMOVE_IN_KST_12193,
            ) ? (
                <InfoCard />
            ) : null}
        </React.Fragment>
    );
};

export default App;
